import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { endpoints } from '../../api';
import type { UserAvatar } from '../../api/people';
import { readHistoryRepeat } from '../../bridge/history-repeat';
import { uiText, useAccountLocale } from '../../i18n';
import { peekHelpStudioTarget, readHelpStudioTarget } from '../help/help-studio-entry';
import { profileStudioEntry } from './profile-entry';
import type { StudioApp, StudioDocument } from './studio-api';
import { resolveStudioTarget } from './resolve-studio-target';
import { Sticky } from '../../ui/Sticky';
import { StudioIcon } from './StudioIcon';
import { StudioScreen } from './StudioScreen';
import { StudioHeaderContext } from './studio-header-context';
import { StudioPreview } from './StudioPreview';
import { cachedStudioPreview, loadStudioPreview } from './studio-preview-store';
import { StudioBrowse } from './StudioBrowse';
import { initialBrowse, browseStateKey, type BrowseState } from './studio-browse-state';
import { recallStudioView, rememberStudioView, studioOwner } from './studio-store';
import { studioStateFromHash } from './studio-state-flags';
import { allowsPrototypeTools } from '../../platform/server-mode';
import { consumeStudioTarget, readStudioTarget, studioTarget, type StudioTarget } from './studio-navigation';
import { documentKind, opensScreen, record, text, priceText } from './studio-content';
import { useStudioAutoplay } from './use-studio-preferences';
import { EngineFrame } from './engine-kit/EngineFrame';
import { engineFor } from './engine-kit/engine-screens';
import { SWAP_MS, pop, reduced } from '../../ui/motion';
import { stickySync } from '../../ui/sticky-core';
import './studio.css';
import './studio-template.css';
import './native-template.css';

/** Каталог и формы публикует сервер; просмотр никогда не создаёт заказ. */
/** Сколько нажатие ждёт документ листа шаблона, прежде чем лист выедет собранным из карточки */
const PREVIEW_WAIT_MS = 120;
/** Поля состояния, от которых зависит выдача страницы: их смена ведёт ленту к началу (toTop эталона) */
const listFields = ['q', 'tab', 'top', 'format', 'cast', 'sort', 'section'] as const;
/** Слои над разделом, которые Escape закрывает сам (лист ядра, панель голосов, лист «Ещё», подсказка): Escape — их, не раздела
    (Escape страницы шаблона, bot-studio.js: «поверх страницы лист, меню «Ещё» или обучалка — Escape их, не наш») */
const LAYERS = '.bsheet.open,.side.open,#msheet.open,#hint.on';
/** Куда встаёт прокрутка нового вида: top — место; restore — вид-адресат (browseStateKey), если место ещё не достать,
    пока выдача не пришла: тогда его ставит onReady */
interface SwapScroll {
  top: number;
  restore?: string;
}
/** Кадр экрана создания: родная форма React или кадр движка шаблона (engine — решение engineFor, взятое в коммите,
    который создал кадр; смонтированный кадр его не меняет) */
interface FormFrame {
  app: StudioApp;
  inputs?: Record<string, unknown>;
  revision: number;
  template: boolean;
  engine: boolean;
}
/** Ключ кадра: родной и движковый кадр одного приложения и ревизии — разные экземпляры */
const frameKey = (frame: FormFrame) => `${frame.app.id}:${frame.revision}:${frame.engine ? 'e' : 'n'}`;
/** Экран приложения открывается движком шаблона (engine-kit): решается один раз, в коммите смены вида */
const engineOf = (app: StudioApp) => typeof app.entry?.screen === 'string' && engineFor(app.entry.screen);
/** Имя экрана движка — как его сверяет engineFor (ENGINES по нижнему регистру) */
const engineScreen = (app: StudioApp) => String(app.entry?.screen ?? '').trim().toLowerCase();
/** Прокрутка экрана создания: колонка React .st-in.ap-in, у кадра движка (#studio.eng) — колонка .ap-in его активного
    хозяина .st-eng (её рисует движок). Читается по разметке: после commit классы уже на месте */
function formBox(main: HTMLElement | null, column: HTMLElement | null) {
  return main?.classList.contains('eng') ? main.querySelector<HTMLElement>(':scope > .st-eng:not([hidden]) > .ap-in') : column;
}
/** Вход вида (pop эталона): .enter снимает конец его собственной анимации — всплывший конец анимации внутри вида её не обрывает */
function enterView(main: HTMLElement) {
  main.classList.remove('enter');
  void main.offsetWidth;
  main.classList.add('enter');
  const end = (event: AnimationEvent) => {
    if (event.target !== main) return;
    main.classList.remove('enter');
    main.removeEventListener('animationend', end);
  };
  main.addEventListener('animationend', end);
}
/** Сбой действия — плашка поверх раздела (стекло подсказки .gtip), а не строка в колонке: страница под ней не сдвигается.
    Чтение с экрана получает её как alert; появление — pop, в заморозке .still и при «уменьшить движение» — сразу */
function StudioToast({ children }: { children: ReactNode }) {
  const box = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!reduced()) pop(box.current);
  }, []);
  return (
    <div ref={box} className="st-toast lg" role="alert">
      {children}
    </div>
  );
}

export function ServerStudio() {
  useAccountLocale();
  const autoplay = useStudioAutoplay();
  const [entry] = useState(() => ({
    help: peekHelpStudioTarget(),
    repeat: readHistoryRepeat('studio'),
    profile: profileStudioEntry(),
    target: readStudioTarget(),
  }));
  /* [ТЕСТ] Состояние из хэша («#studio.atabtwilight»): те же флаги, что у прототипа — нужны для сверки кадров.
     В production-сборку разбор не попадает. */
  const [entryFlags] = useState(() =>
    allowsPrototypeTools() || ((import.meta.env.DEV || import.meta.env.MODE === 'test') && import.meta.env.VITE_MOCK_API === 'true')
      ? studioStateFromHash(location.hash)
      : null,
  );
  /* Вид, фильтры, поиск, стек «Назад» и прокрутка с прошлого показа раздела (studio-store, у своего владельца): mount эталона
     рисует вид из S сразу. Состояние из хэша [ТЕСТ] — вход с нуля */
  const [restored] = useState(() => (entryFlags ? null : recallStudioView()));
  const [state, setState] = useState<BrowseState>(entryFlags?.browse ?? restored?.browse ?? initialBrowse);
  const [selected, setSelected] = useState<StudioApp | null>(null);
  /* текущий кадр — кадр движка шаблона: ставится в тех же коммитах, что selected */
  const [selectedEngine, setSelectedEngine] = useState(false);
  const [preview, setPreview] = useState<number | null>(null);
  /* лист шаблона один на «Студию» и остаётся в документе после закрытия (#stPre шаблона): уход — переход на том же узле */
  const [sheet, setSheet] = useState<{ id: number; tile: Record<string, unknown> } | null>(null);
  /* ожидание документа листа: новое нажатие или уход со страницы отменяет прежнее */
  const previewWait = useRef(0);
  const [avatar, setAvatar] = useState<UserAvatar | null>(null);
  const [inputs, setInputs] = useState<Record<string, unknown>>();
  const [pending, setPending] = useState<StudioTarget | null>(
    /* [ТЕСТ] «#studio.pgo33» открывает экран шаблона сразу, как pgo у эталона (engine.ts:1694) */
    (entryFlags?.appId ? { appId: entryFlags.appId } : null) ??
      entry.target ??
      (entry.repeat?.app_id
        ? { appId: entry.repeat.app_id, options: entry.repeat.inputs }
        : entry.repeat
          ? { screen: entry.repeat.screen, options: entry.repeat.inputs }
          : entry.help
            ? { screen: entry.help.screen, options: entry.help.options }
            : entry.profile
              ? { screen: entry.profile.screen, ...(entry.profile.screen === 'photo' ? { options: { mode: 'generate' } } : {}) }
              : null),
  );
  const [error, setError] = useState('');
  /* Пока цель нажатия разрешается (resolveStudioTarget), вид стоит как есть: у эталона переход синхронный и над страницей
     ничего не вставляется. Занятым помечается только само нажатое — aria-busy, как .btn.busy набора виджетов, без раскладки */
  const tapped = useRef<Element | null>(null);
  const busyFrom = useRef<Element | null>(null);
  const [revision, setRevision] = useState(0);
  const [headerHost, setHeaderHost] = useState<HTMLElement | null>(null);
  const stack = useRef<Array<{ state: BrowseState; scroll: number }>>(restored?.stack.slice() ?? []);
  const root = useRef<HTMLElement | null>(null);
  /** Прокрутка просмотра (#studio) — для памяти раздела: при уходе узлов уже нет */
  const browseScroll = useRef(restored?.scroll ?? 0);
  const scrollDirty = useRef(false);
  /** Прокрутка просмотра — в browseScroll, если была после прошлого чтения */
  const flushScroll = () => {
    const main = root.current;
    if (scrollDirty.current && main && !main.classList.contains('ap')) browseScroll.current = main.scrollTop;
    scrollDirty.current = false;
  };
  const column = useRef<HTMLDivElement | null>(null);
  /* [ТЕСТ] Прокрутка из флага хэша: «#studio.scroll700» и «#studio.blkall». Эталон подводит блок
     к верху минус 12 px через 160 мс после отрисовки (engine.ts:1652). Без прокрутки липкая шапка
     не прилипает и остаётся без класса sc — расходится всё её поддерево. */
  useEffect(() => {
    const target = entryFlags?.scroll ?? (entryFlags?.block ? 0 : null);
    if (target === null) return;
    const timer = setTimeout(() => {
      const column = root.current;
      if (!column) return;
      if (entryFlags?.block) {
        const block = column.querySelector(`[data-blk="${entryFlags.block}"]`);
        if (block) column.scrollTop += block.getBoundingClientRect().top - column.getBoundingClientRect().top - 12;
        return;
      }
      column.scrollTop = entryFlags?.scroll ?? 0;
    }, 160);
    return () => clearTimeout(timer);
  }, [entryFlags]);
  const targetOriginScroll = useRef<number | null>(null);
  const restoreScroll = useRef<{ identity: string; top: number } | null>(null);
  const formScroll = useRef<number | null>(null);
  const [formRevision, setFormRevision] = useState(0);
  const formSequence = useRef(0);
  const formBack = useRef<(() => boolean) | null>(null);
  const registerFormBack = useCallback((handler: () => boolean) => {
    formBack.current = handler;
    return () => {
      if (formBack.current === handler) formBack.current = null;
    };
  }, []);
  /* готовность активного кадра движка (EngineFrame.registerGate): вход вида ждёт её, как formBack — один обработчик у активного кадра */
  const formGate = useRef<(() => Promise<void> | null) | null>(null);
  const registerFormGate = useCallback((gate: () => Promise<void> | null) => {
    formGate.current = gate;
    return () => {
      if (formGate.current === gate) formGate.current = null;
    };
  }, []);
  const [formHistory, setFormHistory] = useState<Array<FormFrame & { scroll: number }>>([]);
  /* экран открыт кнопкой «Попробовать» листа шаблона: у кружка формат и название задаёт шаблон (APP-SPEC-4 §6.3) */
  const [templateEntry, setTemplateEntry] = useState(false);
  /** Прокрутка вида: у просмотра — сам раздел #studio, у экрана создания — formBox() (#studio.ap не прокручивается, app.css) */
  const scroller = () => (root.current?.classList.contains('ap') ? formBox(root.current, column.current) : root.current);
  /* Смена вида — swap эталона (SteosUI.swap на #studio): вид уходит (.leave, vout 160 мс), через SWAP_MS новый вид
     встаёт одним commit, прокрутка (к началу или сохранённое место) ставится в том же commit до первого кадра, пока
     раздел невидим, и вид входит (.enter, vin 220 мс). Состояние до commit не меняется: запрос, пришедший, пока вид
     уходит, заменяет прежний. В заморозке .still и при «уменьшить движение» — сразу, без анимаций. */
  const swapTimer = useRef(0);
  /* Переход, выбранный человеком (navigate, «Попробовать»), снимает разрешение прежнего намерения сразу, а не в commit swap'а:
     ответ, пришедший, пока вид уходит, не перебивает его последнее нажатие */
  const intent = useRef(0);
  const swapScroll = useRef<SwapScroll | null>(null);
  const [swapTick, setSwapTick] = useState(0);
  /** Номер смены вида: вход, ждущий готовность кадра движка, отменяет следующая смена */
  const swapSequence = useRef(0);
  function swapView(commit: () => void, scroll: SwapScroll) {
    swapSequence.current++;
    const main = root.current;
    const run = () => {
      swapTimer.current = 0;
      swapScroll.current = scroll;
      restoreScroll.current = null;
      commit();
      setSwapTick(value => value + 1);
    };
    clearTimeout(swapTimer.current);
    if (!main || reduced()) {
      main?.classList.remove('leave', 'enter');
      run();
      return;
    }
    main.classList.remove('enter');
    main.classList.add('leave');
    swapTimer.current = window.setTimeout(run, SWAP_MS);
  }
  useLayoutEffect(() => {
    const scroll = swapScroll.current;
    if (!scroll) return;
    swapScroll.current = null;
    const main = root.current;
    if (!main) return;
    const enter = () => {
      const box = main.classList.contains('ap') ? formBox(main, column.current) : main;
      main.style.removeProperty('opacity');
      main.style.removeProperty('pointer-events');
      main.classList.remove('leave');
      if (box) {
        /* Нижнее поле колонки экрана (--ap-pad) въезжает переходом 0,35 с: место прежней формы в истории считается
           с ним, поэтому поле встаёт сразу — вид всё равно ещё невидим */
        const transition = box.style.transition;
        box.style.transition = 'none';
        box.scrollTop = scroll.top;
        box.style.transition = transition;
        // места ещё нет (выдача не пришла) — его поставит onReady, когда вид будет готов
        if (scroll.restore && Math.abs(box.scrollTop - scroll.top) > 1) restoreScroll.current = { identity: scroll.restore, top: scroll.top };
        stickySync(box);
      }
      if (!reduced()) enterView(main);
    };
    /* Кадр движка, чьи списки приходят с формой и документом экрана (bound.ready), не входит пустым: невидимый вид ждёт его
       готовность не дольше READY_MS (EngineFrame). Без анимаций (.still, «уменьшить движение») смена мгновенная, как у шаблона:
       ждать под видимым видом нечего, первую отрисовку держит сам движок. Следующая смена вида ожидание отменяет */
    const waiting = reduced() ? null : (formGate.current?.() ?? null);
    if (!waiting) {
      enter();
      return;
    }
    /* вид стоит невидимым и без касаний, как в конце .leave: класс мог снять commit React (className раздела), а новый
       .leave повторил бы vout по видимому виду. vout без from начинает с этого же нуля, vin входа — с нуля */
    main.style.opacity = '0';
    main.style.pointerEvents = 'none';
    const sequence = swapSequence.current;
    void waiting.then(() => {
      if (sequence === swapSequence.current && root.current === main) enter();
    });
  }, [swapTick]);
  useEffect(() => () => clearTimeout(swapTimer.current), []);
  /* Возврат в раздел: прокрутка встаёт на прежнее место в первом же commit, до показа вида (документы и выдачи — из памяти
     раздела, разметка уже полная). Места ещё нет — выдача не помнилась и грузится: его поставит onReady */
  useLayoutEffect(() => {
    const main = root.current;
    if (!restored?.scroll || !main) return;
    main.scrollTop = restored.scroll;
    stickySync(main);
    if (Math.abs(main.scrollTop - restored.scroll) > 1) restoreScroll.current = { identity: browseStateKey(restored.browse), top: restored.scroll };
  }, [restored]);
  /* Уход из раздела (unmount эталона S не трогает): вид, стек «Назад» и прокрутка — в память раздела. Под открытым экраном
     создания помнится место просмотра под ним; сам экран, лист шаблона и ожидание открытия не помнятся */
  /* уход из раздела: место просмотра читается до снятия разметки (очистка useLayoutEffect идёт раньше, чем узлы уходят из документа) */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => () => flushScroll(), []);
  const leaving = useRef({ state, form: false });
  useEffect(() => {
    leaving.current = { state, form: !!selected };
  });
  useEffect(() => {
    const view = leaving,
      back = stack,
      scroll = browseScroll,
      form = formScroll,
      // вид, собранный при другом владельце, новому не достаётся
      owner = studioOwner();
    return () => {
      if (owner === studioOwner())
        rememberStudioView({
          browse: view.current.state,
          stack: back.current.slice(),
          scroll: view.current.form ? (form.current ?? 0) : scroll.current,
        });
    };
  }, []);
  function navigate(next: BrowseState, originScroll?: number) {
    intent.current++;
    previewWait.current++;
    setPreview(null);
    const from = { state, scroll: originScroll ?? scroller()?.scrollTop ?? 0 },
      fromForm = !!selected,
      waiting = pending;
    swapView(
      () => {
        if (fromForm) stack.current = [{ state: initialBrowse, scroll: 0 }];
        else stack.current.push(from);
        setState(next);
        // намерение, пришедшее, пока вид уходил, не снимается
        setPending(current => (current === waiting ? null : current));
        setSelected(null);
        setSelectedEngine(false);
        setError('');
        setFormHistory([]);
        setTemplateEntry(false);
      },
      { top: 0 },
    );
  }
  function back() {
    // вид уже уходит: «назад» в эти 170 мс не копится (у эталона .leave снимает касания с раздела)
    if (swapTimer.current) return;
    previewWait.current++;
    if (pending) {
      setPending(null);
      setError('');
      return;
    }
    if (preview) {
      setPreview(null);
      return;
    }
    if (selected) {
      if (formBack.current?.()) return;
      const previous = formHistory.at(-1);
      if (previous) {
        swapView(
          () => {
            setFormHistory(value => value.slice(0, -1));
            setSelected(previous.app);
            setSelectedEngine(previous.engine);
            setTemplateEntry(previous.template);
            setInputs(previous.inputs);
            setFormRevision(previous.revision);
            setError('');
          },
          { top: previous.scroll },
        );
        return;
      }
      swapView(
        () => {
          formScroll.current = null;
          setSelected(null);
          setSelectedEngine(false);
          setTemplateEntry(false);
          setInputs(undefined);
          setError('');
        },
        { top: formScroll.current ?? 0, restore: browseStateKey(state) },
      );
      return;
    }
    if (state.q || state.searchOpen) {
      changeBrowse({ q: '', searchOpen: false });
      return;
    }
    const previous = stack.current.at(-1);
    if (!previous && state.view === 'home') {
      // возвращаться некуда: главная встаёт в исходное состояние на месте, без смены вида
      restoreScroll.current = { identity: browseStateKey(initialBrowse), top: 0 };
      setState(initialBrowse);
      return;
    }
    const destination = previous?.state ?? initialBrowse;
    swapView(
      () => {
        stack.current.pop();
        setState(destination);
      },
      { top: previous?.scroll ?? 0, restore: browseStateKey(destination) },
    );
  }
  function changeBrowse(change: Partial<BrowseState>, { fit = false }: { fit?: boolean } = {}) {
    const next = { ...state, ...change };
    /* Формат, состав, порядок, вкладка, раздел и поиск страницы: выдача к началу до смены (toTop эталона) —
       прежние карточки встают наверх, и смена выдачи ведёт их оттуда. Поиск главной не прокручивает:
       место поля и возврат на прежнее место держит сама главная (hqPin, StudioBrowse). Поправка по ответу
       новой вкладки (fit) тоже: к началу лента ушла сменой вкладки, а прокрутку за время загрузки сделал человек */
    const column = scroller();
    /* поиск — по словам запроса (q.trim() уходит в запрос): пробелы, снятые закрытием поля, выдачу не меняют (had эталона qClose) */
    const differs = (field: (typeof listFields)[number]) => (field === 'q' ? next.q.trim() !== state.q.trim() : next[field] !== state[field]);
    if (!fit && state.view !== 'home' && listFields.some(differs) && column && column.scrollTop > 0) column.scrollTop = 0;
    // поправка ложится на последнее состояние, а не на то, что было при отрисовке
    setState(current => ({ ...current, ...change }));
  }
  useEffect(() => {
    if (entry.target) consumeStudioTarget(entry.target);
    if (entry.help) readHelpStudioTarget(entry.help);
    sessionStorage.removeItem('steos:studio:profile-entry');
    const receive = (event: Event) => {
      const target = studioTarget((event as CustomEvent).detail);
      if (target) {
        targetOriginScroll.current = scroller()?.scrollTop ?? 0;
        busyFrom.current = null;
        consumeStudioTarget();
        setPending(target);
        setPreview(null);
        setError('');
      }
    };
    document.addEventListener('steos:studio:target', receive);
    return () => document.removeEventListener('steos:studio:target', receive);
  }, [entry.target, entry.help]);
  useEffect(() => {
    const controller = new AbortController();
    const id = entry.repeat?.avatar_id ?? entry.profile?.avatarId;
    if (!id) return;
    void (async () => {
      let before: number | undefined;
      do {
        const page = await endpoints.people.avatars(before);
        if (controller.signal.aborted) return;
        const found = page.items.find(item => item.id === id && item.status === 'ready');
        if (found) {
          setAvatar(found);
          return;
        }
        before = page.next_before_id ?? undefined;
      } while (before);
      if (!controller.signal.aborted) setError('Выбранный вариант недоступен. Вернитесь в профиль и обновите список.');
    })().catch(() => {
      if (!controller.signal.aborted) setError('Не удалось загрузить выбранный вариант.');
    });
    return () => controller.abort();
  }, [entry]);
  useEffect(() => {
    if (!pending) return;
    const controller = new AbortController();
    const deadline = window.setTimeout(() => {
      controller.abort();
      setError('Не удалось открыть инструмент за 30 секунд. Обновите запрос или вернитесь назад.');
    }, 30000);
    // navigate() из этого же ответа идёт после проверки — свой переход он не отменяет
    const token = intent.current,
      stale = () => controller.signal.aborted || token !== intent.current;
    void resolveStudioTarget(pending, controller.signal, entry.help?.title)
      .then(result => {
        if (stale()) return;
        if (result.view)
          navigate(
            {
              ...initialBrowse,
              view: result.view,
              sort: result.view === 'tools' ? 'pop' : initialBrowse.sort,
              section: text(pending.options?.section) || 'all',
            },
            targetOriginScroll.current ?? undefined,
          );
        if (result.collection)
          navigate({ ...initialBrowse, view: 'collection', collection: result.collection }, targetOriginScroll.current ?? undefined);
        const app = result.app;
        if (app && (!selected || app.id !== selected.id || pending.options)) {
          // место, с которого ушли: прокрутка просмотра или колонки текущего экрана
          const origin = targetOriginScroll.current ?? scroller()?.scrollTop ?? 0,
            options = pending.options,
            ret = !!selected && !!pending.ret;
          setPreview(null);
          swapView(
            () => {
              if (selected && ret)
                setFormHistory(value => [
                  ...value,
                  { app: selected, inputs, revision: formRevision, scroll: origin, template: templateEntry, engine: selectedEngine },
                ]);
              else setFormHistory([]);
              if (!selected) formScroll.current = origin;
              setSelected(app);
              setSelectedEngine(engineOf(app));
              setTemplateEntry(false);
              setInputs(options);
              setFormRevision(++formSequence.current);
              setPending(current => (current === pending ? null : current));
            },
            { top: 0 },
          );
        } else if (!result.view && !result.collection) setPending(null);
        /* при смене вида намерение снимается вместе с прежним видом, в commit swap'а: нажатое остаётся занятым, пока вид уходит */
        targetOriginScroll.current = null;
      })
      .catch(failure => {
        if (!stale()) setError(failure instanceof Error ? failure.message : 'Не удалось открыть инструмент.');
      })
      .finally(() => clearTimeout(deadline));
    return () => {
      clearTimeout(deadline);
      controller.abort();
    };
    // Одно намерение выполняется один раз, независимо от текущей страницы.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending, revision]);
  useEffect(() => {
    const node = pending && !selected && !error ? busyFrom.current : null;
    if (!node?.isConnected) return;
    /* вид занятого — по своей метке data-st-busy, не по aria-busy: формы внутри #studio (варианты текста, «Создаём»,
       карточка референса) ставят aria-busy сами и остаются активными, как у эталона */
    node.setAttribute('aria-busy', 'true');
    node.setAttribute('data-st-busy', '');
    return () => {
      node.removeAttribute('aria-busy');
      node.removeAttribute('data-st-busy');
    };
  }, [pending, selected, error]);
  function action(value: unknown) {
    setError('');
    busyFrom.current = tapped.current;
    targetOriginScroll.current = scroller()?.scrollTop ?? 0;
    const a = record(value);
    const section = text(a.sec ?? a.section);
    // Распознавание — инструмент Студии «Текст», а не вкладка «Озвучка»
    if (a.type === 'open_section' && section === 'stt') {
      setPending({ screen: 'text', options: { screen: 'text', tool: 'stt' } });
      return;
    }
    // Кадр «Озвучка проектов» приходит как open_screen { screen: 'script' }: проекты — отдельный раздел, не экран Студии
    const screen = a.type === 'open_screen' ? text(record(a.entry).screen) : '';
    if (screen === 'script' || screen === 'projects') {
      sessionStorage.removeItem('steos:history:repeat');
      sessionStorage.removeItem('steos:studio:profile-entry');
      sessionStorage.removeItem('steos:studio:target');
      location.hash = '#projects';
      return;
    }
    if (a.type === 'open_section' && ['tts', 'clone', 'projects', 'script'].includes(section)) {
      sessionStorage.removeItem('steos:history:repeat');
      sessionStorage.removeItem('steos:studio:profile-entry');
      sessionStorage.removeItem('steos:studio:target');
      location.hash = `#${section === 'script' ? 'projects' : section}`;
      return;
    }
    if (a.type === 'open_tools') {
      navigate({ ...initialBrowse, view: 'tools', sort: 'pop' });
      return;
    }
    if (a.type === 'open_collection') {
      setPending({ collection: String(a.coll ?? a.collection_id ?? '') });
      return;
    }
    if (a.type === 'open_app' && Number.isSafeInteger(a.app_id)) {
      setPending(studioTarget({ appId: Number(a.app_id), options: a.options }));
      return;
    }
    if (a.type === 'open_screen') {
      const entry = record(a.entry);
      const target = Object.keys(entry).length ? studioTarget(entry) : null;
      if (target) {
        setPending(target);
        return;
      }
    }
    setError('Это действие пока недоступно.');
  }
  /** Лист шаблона выезжает уже заполненным, как у шаблона: документ обычно загружен с касания карточки (studio-preview-store);
      нет — ждём его до PREVIEW_WAIT_MS, затем лист выезжает собранным из карточки и дополняется на месте */
  function showPreview(item: StudioDocument) {
    const id = item.id,
      token = ++previewWait.current;
    let done = false;
    const show = () => {
      if (done || previewWait.current !== token) return;
      done = true;
      setSheet({ id, tile: item as unknown as Record<string, unknown> });
      setPreview(id);
    };
    if (cachedStudioPreview(id)) return show();
    const timer = window.setTimeout(show, PREVIEW_WAIT_MS);
    const settle = () => {
      clearTimeout(timer);
      show();
    };
    loadStudioPreview(id).then(settle, settle);
  }
  function open(item: StudioDocument) {
    setError('');
    busyFrom.current = tapped.current;
    previewWait.current++;
    const kind = documentKind(item);
    if (kind === 'collection') navigate({ ...initialBrowse, view: 'collection', collection: item });
    else if (kind === 'tool') action(item.action ?? { type: 'open_screen', entry: item.entry });
    else if (kind === 'app' && opensScreen(item)) setPending({ appId: item.id });
    else if (kind === 'reel_template') {
      const appId = record(item.entry).app_id;
      if (Number.isSafeInteger(appId) && Number(appId) > 0) setPending({ appId: Number(appId) });
      else setError('Это действие пока недоступно.');
    } else showPreview(item);
  }
  useEffect(() => {
    /* слой над разделом снимается до нажатия — в фазе захвата окна, раньше слушателей документа, которые его закрывают (closeSide шелла) */
    let layered = false;
    const before = (event: KeyboardEvent) => {
      if (event.key === 'Escape') layered = !!document.querySelector(LAYERS);
    };
    const keyboard = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !event.defaultPrevented && !layered) {
        event.preventDefault();
        back();
      }
    };
    window.addEventListener('keydown', before, true);
    document.addEventListener('keydown', keyboard);
    return () => {
      window.removeEventListener('keydown', before, true);
      document.removeEventListener('keydown', keyboard);
    };
  });
  /* кадры экрана создания ждут выбранный вариант профиля, если вход его назвал */
  const framesReady = !(entry.repeat?.avatar_id ?? entry.profile?.avatarId) || !!avatar;
  const frames: FormFrame[] = selected
    ? [...formHistory, { app: selected, inputs, revision: formRevision, template: templateEntry, engine: selectedEngine }]
    : [];
  /* Текущий кадр — движок шаблона: колонка React спрятана (hidden, inert) и без шапки, #studio.eng отдаёт поля хозяину .st-eng
     (engine-kit/engine.css); родные кадры остаются в колонке, кадры движка стоят после неё */
  const engineView = !!selected && selectedEngine && framesReady;
  return (
    <StudioHeaderContext.Provider value={headerHost}>
      <main
        ref={root}
        id="studio"
        className={`pg st${selected ? ' ap' : state.view !== 'home' ? ' tl' : ''}${engineView ? ' eng' : ''}`}
        onClickCapture={event => {
          tapped.current = event.target instanceof Element ? event.target.closest('button,[role="button"],a') : null;
          flushScroll();
        }}
        onScroll={event => {
          // прокрутка просмотра; у экрана создания прокручивается его колонка, а #studio.ap стоит.
          // Место читается не на каждое событие, а когда нужно (нажатие, уход из раздела — flushScroll): чтение scrollTop посреди кадра, где сборка
          // аватарок и слайдер уже поменяли стили, заставляло браузер раскладывать главную заново на каждое событие (замер на ПК: 77 раз, 85 мс за жест)
          if (!event.currentTarget.classList.contains('ap')) scrollDirty.current = true;
        }}
      >
        {/* класс колонки — по виду: на нём держатся правила кладки и размеры пустой сцены (bot-studio.css) */}
        <div
          ref={column}
          className={`st-in${
            selected
              ? ' ap-in'
              : state.view === 'home'
                ? ' st-home'
                : state.view === 'catalog'
                  ? ' st-catalog'
                  : state.view === 'collection'
                    ? ' st-cpg'
                    : ' st-tlp'
          }`}
          hidden={engineView}
          inert={engineView}
        >
          {selected ? (
            <>
              {!engineView && (
                <Sticky className="st-top ap-top" flush>
                  <button type="button" className="iconb xbtn" data-studio-back onClick={back} aria-label={uiText('Назад')}>
                    <StudioIcon kind="back" />
                  </button>
                  <div className="bc-tt">
                    <div className="studio-default-heading">
                      <h2 className="pf-title">{selected.name}</h2>
                      {priceText(selected.price) && <span className="bc-unit">{priceText(selected.price)}</span>}
                    </div>
                    <div id="studio-header-slot" ref={setHeaderHost} />
                  </div>
                </Sticky>
              )}
              {avatar && (
                <p>
                  {uiText('Выбранный вариант')}: {avatar.variant_name}
                </p>
              )}
              {framesReady
                ? frames.filter(frame => !frame.engine).map(frame => (
                    <div
                      key={frameKey(frame)}
                      className="st-form-host"
                      hidden={frame.revision !== formRevision}
                      inert={frame.revision !== formRevision}
                    >
                      <StudioScreen
                        app={frame.app}
                        avatar={avatar}
                        initialInputs={frame.inputs}
                        templateEntry={frame.template}
                        onBack={back}
                        registerBack={frame.revision === formRevision ? registerFormBack : undefined}
                        active={frame.revision === formRevision}
                      />
                    </div>
                  ))
                : !error && <p role="status">{uiText('Загружаем…')}</p>}
            </>
          ) : null}
          <div className="st-template-browse" hidden={!!selected} inert={!!selected}>
            <StudioBrowse
              sortOpenAtStart={!!entryFlags?.sortOpen}
              heroPaused={!!selected || preview !== null}
              state={state}
              onChange={changeBrowse}
              onView={view =>
                navigate({
                  ...initialBrowse,
                  view,
                  format: state.view === 'home' && view === 'catalog' ? state.format : 'all',
                  sort: view === 'tools' ? 'pop' : initialBrowse.sort,
                  q: state.view === 'collection' && view === 'catalog' ? state.q : '',
                })
              }
              onOpen={open}
              onAction={action}
              onBack={back}
              onReady={ready => {
                /* Запасной путь: место возврата не достать в commit смены вида (выдача адресата ещё грузилась) —
                   ставим, как только вид готов. Обычно место встаёт в самой смене вида (swapView) */
                const saved = restoreScroll.current,
                  box = root.current;
                // просмотр под открытым экраном тоже отрисовывается: колонку экрана его место не трогает
                if (saved?.identity === browseStateKey(ready) && !box?.classList.contains('ap')) {
                  restoreScroll.current = null;
                  if (box) {
                    box.scrollTop = saved.top;
                    stickySync(box);
                  }
                }
              }}
            />
          </div>
          {sheet && (
            <StudioPreview
              appId={sheet.id}
              tile={sheet.tile}
              open={preview === sheet.id}
              autoplay={autoplay}
              onClose={() => setPreview(null)}
              onTry={(app, roleId) => {
                /* preGo эталона: лист закрывается сразу (он вне раздела), раздел меняет вид swap'ом */
                const position = scroller()?.scrollTop ?? 0,
                  waiting = pending;
                intent.current++;
                setPreview(null);
                swapView(
                  () => {
                    formScroll.current = position;
                    setPending(current => (current === waiting ? null : current));
                    setSelected(app);
                    setSelectedEngine(engineOf(app));
                    setTemplateEntry(true);
                    setInputs(roleId ? { open_role: roleId } : undefined);
                    setFormHistory([]);
                    setFormRevision(++formSequence.current);
                  },
                  { top: 0 },
                );
              }}
            />
          )}
        </div>
        {framesReady &&
          frames.filter(frame => frame.engine).map(frame => (
            <EngineFrame
              key={frameKey(frame)}
              frameKey={frameKey(frame)}
              screen={engineScreen(frame.app)}
              app={frame.app}
              avatar={avatar}
              initialInputs={frame.inputs}
              templateEntry={frame.template}
              active={frame.revision === formRevision}
              onBack={back}
              registerBack={frame.revision === formRevision ? registerFormBack : undefined}
              registerGate={frame.revision === formRevision ? registerFormGate : undefined}
            />
          ))}
      </main>
      {/* Ни ожидание, ни сбой не встают в колонку раздела: вставка над видом сдвигала всю страницу. «Загружаем…» слышит
          только чтение с экрана — и только пока экран ещё не открыт: открытый экран показывает своё состояние сам,
          в том числе ошибку формы (на dev-стенде /app/studio/apps/{id}/form отвечает 503 products_unavailable) */}
      {pending && !selected && !error && (
        <>
          <p role="status" className="sr-only">
            {uiText('Загружаем…')}
          </p>
          {/* «Назад» Telegram (backAct шелла) ищет видимую кнопку data-studio-back в #view: пока цель разрешается, на главной
              другой нет — без этой Telegram показал бы «Закрыть» вместо отмены. Места не занимает, стоит у верха #view */}
          <button type="button" className="sr-only st-wait-back" data-studio-back tabIndex={-1} onClick={back}>
            {uiText('Отмена')}
          </button>
        </>
      )}
      {error && (
        <StudioToast key={error}>
          <p>{uiText(error)}</p>
          <span className="acts">
            <button
              type="button"
              onClick={() => {
                setError('');
                if (pending) setRevision(value => value + 1);
              }}
            >
              {uiText(pending ? 'Обновить' : 'Закрыть')}
            </button>
            {pending && (
              /* «Назад» Telegram, пока открытие не удалось, — та же отмена */
              <button
                type="button"
                data-studio-back
                onClick={() => {
                  setPending(null);
                  setError('');
                }}
              >
                {uiText('Отмена')}
              </button>
            )}
          </span>
        </StudioToast>
      )}
    </StudioHeaderContext.Provider>
  );
}
